import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RoleMatrix, RoleMatrixEntry } from '@azko/shared';

import { ApiRequestError, api } from '../../lib/api.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  LoadingRows,
  PageHeader,
  StatusBadge,
  TextInput,
  cx,
} from '../../components/ui.js';

/**
 * Papeis e permissoes — o que cada cargo pode fazer.
 *
 * `plataforma:papeis:gerenciar` existia na matriz desde o RBAC e nunca teve
 * rota nem tela. Os ROTULOS desta pagina ja estavam escritos em
 * `shared/src/rbac.ts`, com o comentario "Rotulos da tela de papeis" — a tela e
 * que nunca foi construida. Mudar o que um cargo pode fazer exigia editar o
 * seed e fazer deploy, no produto que vende exatamente a ideia de que
 * configuracao e dado e nao codigo.
 *
 * A grade e por PAPEL e nao por permissao, porque e assim que a pergunta chega:
 * "o que o porteiro pode fazer?", nunca "quem pode registrar encomenda?". As
 * permissoes ficam agrupadas pelo primeiro segmento do codigo — que e o produto
 * de origem — para que ligar um modulo novo nao espalhe caixas pela tela toda.
 *
 * O numero de OCUPANTES fica ao lado do nome do papel de proposito: desmarcar
 * uma caixa em `concierge` muda o dia de trabalho de todos os porteiros da
 * base, e esse numero e a diferenca entre um ajuste e um incidente.
 */

function agrupaPorProduto(permissions: RoleMatrix['permissions']) {
  const groups = new Map<string, RoleMatrix['permissions']>();

  for (const permission of permissions) {
    const list = groups.get(permission.namespace) ?? [];
    list.push(permission);
    groups.set(permission.namespace, list);
  }

  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function RolesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'papeis'],
    queryFn: () => api.get<RoleMatrix>('/admin/papeis'),
  });

  const save = useMutation({
    mutationFn: (role: RoleMatrixEntry) =>
      api.put<RoleMatrix>(`/admin/papeis/${role.code}`, {
        permissions: [...draft],
        reason: reason.trim(),
      }),
    onSuccess: () => {
      close();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'papeis'] });
    },
    onError: (cause) => {
      setFailure(
        cause instanceof ApiRequestError ? cause.message : 'Nao consegui salvar a matriz.',
      );
    },
  });

  function close() {
    setEditing(null);
    setDraft(new Set());
    setReason('');
    setFailure(null);
  }

  function startEdit(role: RoleMatrixEntry) {
    setEditing(role.code);
    setDraft(new Set(role.permissions));
    setReason('');
    setFailure(null);
  }

  function toggle(code: string) {
    setDraft((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Papeis e permissoes" />
        <LoadingRows rows={5} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Papeis e permissoes" />

      <Card className="p-5 sm:p-6">
        <p className="eyebrow">Fonte da verdade</p>
        <p className="mt-2 text-sm text-muted">
          {/* Vale dizer em voz alta na propria tela: quem le o codigo espera que
              `shared/src/rbac.ts` mande, e nao manda — ele e o seed e os tipos.
              Em runtime quem decide e o banco, que e o que torna esta tela
              possivel. */}
          O que vale em runtime e esta matriz, lida do banco a cada request (com cache de um
          minuto por processo). O arquivo de RBAC no codigo e o padrao inicial e a lista de
          nomes validos — nao o que esta valendo agora.
        </p>
      </Card>

      {(['platform', 'condominium'] as const).map((scope) => {
        const roles = data.roles.filter((role) => role.scope === scope);
        if (roles.length === 0) return null;

        const permissions = data.permissions.filter((item) => item.scope === scope);

        return (
          <Card key={scope}>
            <CardHeader
              title={scope === 'platform' ? 'Equipe Azko' : 'Dentro do condominio'}
              subtitle={
                scope === 'platform'
                  ? 'Cargos de quem trabalha na plataforma'
                  : 'Cargos de quem trabalha no predio do cliente'
              }
            />

            <ul className="divide-y divide-line-soft border-t border-line-soft">
              {roles.map((role) => {
                const aberta = editing === role.code;

                return (
                  <li key={role.code} className="p-4 sm:px-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2">
                          <span className="font-bold">{role.name}</span>
                          <span className="font-mono text-xs text-faint">{role.code}</span>
                          <StatusBadge tone={role.holders > 0 ? 'accent' : 'neutral'}>
                            {role.holders} {role.holders === 1 ? 'pessoa' : 'pessoas'}
                          </StatusBadge>
                        </p>
                        {role.description && (
                          <p className="mt-0.5 text-sm text-muted">{role.description}</p>
                        )}
                        <p className="mt-1 text-sm text-faint">
                          {role.permissions.length}{' '}
                          {role.permissions.length === 1 ? 'permissao' : 'permissoes'}
                        </p>
                      </div>

                      <Button
                        variant="quiet"
                        className="shrink-0 py-1.5"
                        onClick={() => (aberta ? close() : startEdit(role))}
                      >
                        {aberta ? 'Fechar' : 'Editar'}
                      </Button>
                    </div>

                    {aberta && (
                      <div className="mt-4 space-y-4 rounded-2xl border border-line bg-surface-2 p-4">
                        {role.holders > 0 && (
                          <p className="text-sm text-warn">
                            {role.holders}{' '}
                            {role.holders === 1 ? 'pessoa ocupa' : 'pessoas ocupam'} este papel. O
                            que voce mudar aqui vale para {role.holders === 1 ? 'ela' : 'todas'} no
                            proximo minuto.
                          </p>
                        )}

                        {agrupaPorProduto(permissions).map(([namespace, items]) => (
                          <fieldset key={namespace}>
                            <legend className="text-xs font-bold uppercase tracking-wider text-muted">
                              {namespace}
                            </legend>
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                              {items.map((permission) => (
                                <label
                                  key={permission.code}
                                  className="flex items-start gap-2 text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={draft.has(permission.code)}
                                    onChange={() => toggle(permission.code)}
                                    className="mt-0.5 size-4 shrink-0 accent-accent"
                                  />
                                  <span className="min-w-0">
                                    <span className="block">
                                      {permission.label || permission.code}
                                    </span>
                                    <span className="block font-mono text-[0.7rem] text-faint">
                                      {permission.code}
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </fieldset>
                        ))}

                        <TextInput
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          placeholder="Por que esta mudando (obrigatorio)"
                          aria-label="Motivo da mudanca de permissoes"
                          maxLength={255}
                        />

                        {failure && <Alert>{failure}</Alert>}

                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            className="py-1.5"
                            disabled={reason.trim().length < 3}
                            loading={save.isPending}
                            onClick={() => save.mutate(role)}
                          >
                            Salvar {draft.size}{' '}
                            {draft.size === 1 ? 'permissao' : 'permissoes'}
                          </Button>
                          <Button variant="ghost" className="py-1.5" onClick={close}>
                            Cancelar
                          </Button>

                          <span
                            className={cx(
                              'text-sm',
                              draft.size === role.permissions.length
                                ? 'text-faint'
                                : 'text-warn',
                            )}
                          >
                            {draft.size === role.permissions.length
                              ? 'sem mudancas no total'
                              : `era ${role.permissions.length}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}
    </>
  );
}
